/** `x-widget: "import-source"` — pick the TO's import source for `source_id`.
 *
 * A compact single-select over the full import-source list. Not freeSolo: the
 * value must be an existing source. The stored value is the source `code`; the
 * label shows `name` with the code as a secondary hint.
 */
import { Autocomplete, Box, TextField, Typography } from '@mui/material';

import type { ImportSource } from '@/api/integrations';

import { ImportKeyFlags } from './ImportKeyFlags';
import { useImportSources } from './hooks';

interface Props {
  toId: number;
  value: string | null;
  onChange: (code: string | null) => void;
  readOnly?: boolean;
  /** Catalogue kinds the caller is about to write. When given, each option
   *  shows what the key may write and marks anything it is short of — a key
   *  that cannot create tours imports nothing a product sync produces, and
   *  says so only in a debug log. */
  requiredEntities?: string[];
}

export function ImportSourceWidget({
  toId,
  value,
  onChange,
  readOnly = false,
  requiredEntities,
}: Props) {
  const { data: sources, isLoading, isError } = useImportSources(toId);
  const options = sources ?? [];
  const selected = value != null ? (options.find((s) => s.code === value) ?? null) : null;
  // Listing import sources needs `console_restech_view`, which not every
  // surface offering this widget requires of its own route — say so rather
  // than rendering an empty dropdown that looks like "no sources exist".
  const error = isError ? 'Couldn’t load import sources (needs console_restech_view).' : undefined;

  return (
    <Autocomplete<ImportSource, false, false, false>
      size="small"
      disabled={readOnly}
      loading={isLoading}
      options={options}
      value={selected}
      onChange={(_, v) => onChange(v?.code ?? null)}
      getOptionLabel={(o) => o.name || o.code}
      isOptionEqualToValue={(a, b) => a.code === b.code}
      renderOption={(props, o) => (
        <Box component="li" {...props} key={o.id} sx={{ fontSize: 13 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {o.name || o.code}
              {!o.is_enabled && (
                <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 1 }}>
                  (disabled)
                </Typography>
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {o.code}
            </Typography>
            <Box sx={{ mt: 0.25 }}>
              <ImportKeyFlags flags={o.allow_import} required={requiredEntities} dense />
            </Box>
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField {...params} placeholder="Import source…" error={isError} helperText={error} />
      )}
    />
  );
}
